import { useState } from "react";
import { Button, Card, CardContent, CardHeader, CardTitle, Badge, Separator } from "@wa/ui";
import { Monitor, Smartphone } from "lucide-react";
import { useT, useI18n } from "../../../i18n/I18nProvider";
import { api } from "../../../lib/api";
import { fmt, type SessionRow } from "./lib";

// ── Sessions ──────────────────────────────────────────────────────────────
export function SessionsCard({ sessions, onChange }: { sessions: SessionRow[]; onChange: () => void }) {
  const t = useT();
  const { locale } = useI18n();
  const [busyId, setBusyId] = useState<string | null>(null);
  const hasOthers = sessions.some((s) => !s.current);

  async function revoke(id: string) {
    setBusyId(id);
    try {
      await api.del(`/account/sessions/${id}`);
      onChange();
    } finally {
      setBusyId(null);
    }
  }
  async function revokeOthers() {
    setBusyId("others");
    try {
      await api.post("/account/sessions/revoke-others");
      onChange();
    } finally {
      setBusyId(null);
    }
  }

  function isMobile(ua: string | null): boolean {
    return !!ua && /Mobile|Android|iPhone|iPad/.test(ua);
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("security.sessions")}</CardTitle>
        <p className="text-sm text-muted-foreground">{t("security.sessionsHint")}</p>
      </CardHeader>
      <CardContent className="space-y-2">
        {sessions.map((s) => {
          const Icon = isMobile(s.userAgent) ? Smartphone : Monitor;
          return (
            <div key={s.id} className="flex items-center justify-between gap-3 rounded-lg border border-border px-3 py-2.5">
              <div className="flex min-w-0 items-center gap-3">
                <Icon className="size-4 shrink-0 text-muted-foreground" />
                <div className="min-w-0">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <span className="truncate">{s.deviceName || t("security.unknownDevice")}</span>
                    {s.current && <Badge variant="secondary">{t("security.thisDevice")}</Badge>}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">
                    {s.ip || "-"} · {t("security.lastActive", { date: fmt(s.lastActivityAt ?? s.createdAt, locale) })}
                  </div>
                </div>
              </div>
              {!s.current && (
                <Button variant="ghost" size="sm" loading={busyId === s.id} onClick={() => revoke(s.id)}>
                  {t("security.revoke")}
                </Button>
              )}
            </div>
          );
        })}
        {hasOthers && (
          <>
            <Separator className="my-1" />
            <Button variant="outline" size="sm" loading={busyId === "others"} onClick={revokeOthers}>
              {t("security.revokeOthers")}
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}
